import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import {
  tbodyBorder,
  theadBackground,
  theadBorder,
  theadText,
} from "./ColorTheme";
import { AssignmentIndOutlined } from "@mui/icons-material";

export interface ColumnConfig<T> {
  key: keyof T;
  label: string;
  width?: number | string;
  searchable?: boolean; // enable click-to-search
  render?: (row: T) => React.ReactNode; // custom render
}

interface GenericTableProps<T> {
  data: T[];
  columns: ColumnConfig<T>[];
  actions?: {
    edit?: (row: T) => void;
    delete?: (row: T) => void;
    view?: (row: T) => void;
    assign?: (row: T) => void;
  };
}

export function GenericTable<T extends Record<string, any>>({
  data,
  columns,
  actions,
}: GenericTableProps<T>) {
  const [searchValues, setSearchValues] = React.useState<
    Record<string, string>
  >({});

  const [searchMode, setSearchMode] = React.useState<Record<string, boolean>>(
    {}
  );

  const filteredData = React.useMemo(() => {
    return data.filter((row) =>
      columns.every((col) => {
        if (!col.searchable) return true;

        const searchText = searchValues[col.key as string] ?? "";
        if (!searchText) return true;

        const value = row[col.key];
        const valueStr = value != null ? String(value) : ""; // safe conversion

        return valueStr.toLowerCase().includes(searchText.toLowerCase());
      })
    );
  }, [data, searchValues, columns]);

  return (
    <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: theadBackground,
              "& th": {
                color: theadText,
                fontVariant: "small-caps",
                borderRight: `1px solid ${theadBorder}`,
                "&:last-child": { borderRight: "none" },
              },
            }}
          >
            {columns.map((col) => (
              <TableCell
                key={col.key as string}
                onClick={() => {
                  if (col.searchable)
                    setSearchMode((prev) => ({
                      ...prev,
                      [col.key as string]: !prev[col.key as string],
                    }));
                }}
                sx={{
                  cursor: col.searchable ? "pointer" : "default",
                  minWidth: col.width ?? 100,
                  fontWeight: "bold",
                }}
              >
                {searchMode[col.key as string] ? (
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder={`Search ${col.label}`}
                    fullWidth
                    value={searchValues[col.key as string] ?? ""}
                    onChange={(e) =>
                      setSearchValues((prev) => ({
                        ...prev,
                        [col.key]: e.target.value,
                      }))
                    }
                    onBlur={() =>
                      setSearchMode((prev) => ({ ...prev, [col.key]: false }))
                    }
                    autoFocus
                  />
                ) : (
                  col.label
                )}
              </TableCell>
            ))}

            {actions && <TableCell align="center">Actions</TableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredData.map((row, idx) => (
            <TableRow key={idx}>
              {columns.map((col) => (
                <TableCell
                  key={col.key as string}
                  sx={{ borderRight: `1px solid ${tbodyBorder}` }}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </TableCell>
              ))}

              {actions && (
                <TableCell align="center">
                  {actions.edit && (
                    <Tooltip title="Edit">
                      <IconButton
                        color="success"
                        size="small"
                        onClick={() => actions.edit!(row)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {actions.delete && (
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => actions.delete!(row)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {actions.view && (
                    <Tooltip title="View">
                      <IconButton
                        color="info"
                        size="small"
                        onClick={() => actions.view!(row)}
                      >
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {actions.assign && (
                    <Tooltip title="Assign">
                      <IconButton
                        color="info"
                        size="small"
                        onClick={() => actions.assign!(row)}
                      >
                        <AssignmentIndOutlined />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}

          {filteredData.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length + (actions ? 1 : 0)}
                align="center"
                sx={{ py: 4 }}
              >
                No records found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
